import { lazy, Suspense, useEffect, useState } from 'react'
import { c, Fout, haalSessie, LEEG, opUitgelogd } from './lib/api'
import { navigeer, useRoute } from './lib/router'
import { lees, meld, meldFout, useStaat, zet, zetIk } from './lib/staat'
import Poort from './onderdelen/Poort'
import Shell from './onderdelen/Shell'
import Chat from './onderdelen/chat/Chat'
import { Laden } from './onderdelen/ui'

/* De schermen naast het gesprek pas laden als iemand ze opent: wie Cowork
   opent, wil meestal alleen iets vragen. */
const Taken = lazy(() => import('./onderdelen/taken/Taken'))
const TaakEditor = lazy(() => import('./onderdelen/taken/TaakEditor'))
const Over = lazy(() => import('./onderdelen/Over'))

type Stand = 'kijken' | 'binnen' | 'buiten'

export default function App() {
  const [stand, setStand] = useState<Stand>('kijken')
  const ik = useStaat((s) => s.ik)

  /* Een fout uit de adresbalk (?fout=geen-toegang) wordt één keer getoond en
     dan weggehaald, zodat hij er na verversen niet nog steeds staat. */
  const [fout, setFout] = useState(() => new URLSearchParams(location.search).get('fout'))
  useEffect(() => {
    if (!new URLSearchParams(location.search).has('fout')) return
    const u = new URL(location.href)
    u.searchParams.delete('fout')
    history.replaceState(null, '', u.pathname + u.search)
  }, [])

  useEffect(() => {
    opUitgelogd(() => { zet({ ik: null }); setStand('buiten') })

    void (async () => {
      /* Eerst het koekje: dat vraagt de Worker zonder de database. Pas als
         dat klopt, de rest -- waar je bij kunt, de pc op kantoor, wat je
         zou kunnen vragen. */
      const sessie = await haalSessie()
      if (!sessie) { setStand('buiten'); return }
      zet({ ik: sessie.ik })
      setStand('binnen')
      try {
        zetIk(await c('ik', LEEG))
      } catch (e) {
        /* Wel ingelogd bij het dashboard, maar Cowork staat voor deze
           persoon niet aan: dan het inlogscherm met die uitleg, en niet
           een leeg scherm dat bij elke vraag een fout geeft. */
        if (e instanceof Fout && e.status === 403) {
          zet({ ik: null })
          setFout('geen-toegang')
          setStand('buiten')
          return
        }
        if (e instanceof Fout && e.status === 401) return
        zet({ ikGeladen: true })
        meldFout(e)
      }
    })()
  }, [])

  if (stand === 'kijken') return <div className="opstart"><Laden /></div>
  if (stand === 'buiten' || !ik) return <Poort fout={fout} />

  return (
    <Shell>
      <Suspense fallback={<div className="pagina-scroll"><Laden /></div>}>
        <Scherm />
      </Suspense>
    </Shell>
  )
}

function Scherm() {
  const { delen } = useRoute()
  const [eerste, tweede] = delen

  switch (eerste) {
    /* Het nieuwe gesprek en een bestaand gesprek zijn hetzelfde onderdeel op
       dezelfde plek, zodat React het niet weggooit als het adres na de
       eerste vraag van / naar /c/... gaat: het antwoord dat binnenkomt
       hoort in het scherm dat er al stond. */
    case undefined: return <Chat id={null} />
    case 'c': return tweede ? <Chat id={tweede} /> : <Chat id={null} />
    case 'taken':
      if (!tweede) return <Taken />
      return <TaakEditor id={tweede === 'nieuw' ? null : tweede} />
    case 'over': return <Over />
    default: return <NietGevonden />
  }
}

function NietGevonden() {
  useEffect(() => {
    /* Een oud of verkeerd adres: terug naar het begin, maar wel met een
       melding, anders lijkt het of de link werkte. */
    if (lees().ik) {
      meld('Dat adres bestaat hier niet (meer). Je bent terug bij een nieuw gesprek.', 'info')
      navigeer('/', true)
    }
  }, [])
  return null
}
